import { msalInstance, apiScopes, API_BASE } from "../auth/msalConfig";
import type {
  MeResponse,
  Category,
  CostCentre,
  ExpenseRequestSummary,
  ExpenseRequestDetail,
  ExpenseLine,
  ExpenseAttachment,
  NewLineInput,
} from "./types";

async function getToken(): Promise<string> {
  const account = msalInstance.getAllAccounts()[0];
  if (!account) throw new Error("Not signed in");
  const result = await msalInstance.acquireTokenSilent({ scopes: apiScopes, account });
  return result.accessToken;
}

async function call<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...init.headers,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      detail = j.detail ?? j.title ?? detail;
    } catch {}
    throw new Error(`${res.status}: ${detail}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

async function uploadBinary<T = unknown>(path: string, file: File): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    body: file,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": file.type || "application/octet-stream",
      "x-file-name": encodeURIComponent(file.name),
    },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      detail = j.detail ?? j.title ?? detail;
    } catch {}
    throw new Error(`${res.status}: ${detail}`);
  }
  return res.json();
}

export interface UserSearchResult {
  oid: string;
  displayName: string;
  email: string;
}

export const api = {
  me: () => call<MeResponse>("/api/me"),
  categories: () => call<Category[]>("/api/categories"),
  costCentres: () => call<CostCentre[]>("/api/cost-centres"),
  mileageRates: () => call<Array<{ id: number; vehicle_type: "car" | "company_car" | "motorbike" | "bicycle"; tier_min_miles: number; tier_max_miles: number | null; rate_pence: number; passenger_rate_pence: number | null }>>("/api/mileage-rates"),
  mileageLocations: () => call<Array<{ name: string; postcode: string }>>("/api/mileage-locations"),
  mileageDistance: (from: string, to: string) =>
    call<{ from: string; to: string; meters: number; miles: number }>(
      `/api/mileage-distance?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    ),
  emailPrefs: {
    get: () =>
      call<{
        notify_payroll_queue: boolean;
        notify_when_to_review: boolean;
        notify_my_approved: boolean;
        notify_my_rejected: boolean;
        notify_my_paid: boolean;
        notify_my_reverted: boolean;
        notify_my_reminders: boolean;
      }>("/api/me/email-prefs"),
    set: (body: Record<string, boolean>) =>
      call<Record<string, boolean>>("/api/me/email-prefs", { method: "PUT", body: JSON.stringify(body) }),
  },
  searchUsers: (q: string) => call<UserSearchResult[]>(`/api/users/search?q=${encodeURIComponent(q)}`),

  expenses: {
    list: (params: { status?: string; from?: string; to?: string; page?: number } = {}) => {
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => v != null && qs.set(k, String(v)));
      return call<{ items: ExpenseRequestSummary[]; page: number; page_size: number }>(
        `/api/expenses${qs.toString() ? `?${qs}` : ""}`,
      );
    },
    get: (id: string) => call<ExpenseRequestDetail>(`/api/expenses/${id}`),
    create: (body: { cost_centre_id: number; manager_entra_oid?: string | null; manager_email?: string | null; manager_overridden?: boolean; manager_override_reason?: string | null }) =>
      call<ExpenseRequestSummary>("/api/expenses", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: Partial<{ cost_centre_id: number; manager_entra_oid: string | null; manager_email: string | null; manager_overridden: boolean; manager_override_reason: string | null }>) =>
      call<ExpenseRequestSummary>(`/api/expenses/${id}`, { method: "PUT", body: JSON.stringify(body) }),
    delete: (id: string) => call<void>(`/api/expenses/${id}`, { method: "DELETE" }),
    addLine: (id: string, line: NewLineInput) =>
      call<ExpenseLine>(`/api/expenses/${id}/lines`, { method: "POST", body: JSON.stringify(line) }),
    updateLine: (id: string, lineId: string, line: NewLineInput) =>
      call<ExpenseLine>(`/api/expenses/${id}/lines/${lineId}`, { method: "PUT", body: JSON.stringify(line) }),
    deleteLine: (id: string, lineId: string) =>
      call<void>(`/api/expenses/${id}/lines/${lineId}`, { method: "DELETE" }),
    submit: (id: string) => call<ExpenseRequestSummary>(`/api/expenses/${id}/actions/submit`, { method: "POST", body: "{}" }),
    recall: (id: string, reason?: string) =>
      call<{ status: string }>(`/api/expenses/${id}/actions/recall`, { method: "POST", body: JSON.stringify({ reason }) }),
    reopen: (id: string) =>
      call<ExpenseRequestSummary>(`/api/expenses/${id}/actions/reopen`, { method: "POST", body: "{}" }),

    uploadAttachment: (id: string, file: File, lineId?: string) =>
      uploadBinary<{ id: string; line_id: string | null; file_name: string; content_type: string; size_bytes: number; uploaded_at: string; duplicate_of: any }>(
        `/api/expenses/${id}/attachments${lineId ? `?lineId=${lineId}` : ""}`,
        file,
      ),
    attachmentDownloadUrl: (id: string, attachmentId: string) =>
      call<{ url: string; file_name: string; content_type: string }>(`/api/expenses/${id}/attachments/${attachmentId}/download`),
    deleteAttachment: (id: string, attachmentId: string) =>
      call<void>(`/api/expenses/${id}/attachments/${attachmentId}`, { method: "DELETE" }),

    createMissingReceiptDeclaration: (
      id: string,
      lineId: string,
      body: {
        goods_description: string;
        vendor_name: string;
        printed_name: string;
        department?: string | null;
        declared_lost: true;
        declared_no_other_claim: true;
      },
    ) =>
      call<ExpenseAttachment>(`/api/expenses/${id}/lines/${lineId}/missing-receipt`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },
};

export type { MeResponse } from "./types";
